/**
 * Un formulario en uso **tiene dueño**: mientras la persona lo tiene delante, nada que llegue de
 * una petición lo abre, lo reinicia ni lo cierra.
 *
 * Sale de la #190 («Mi trabajo de hoy») y de una decisión de dirección del 10/10/2026, después de
 * que la revisión automática encontrara cuatro veces el mismo defecto en la misma pantalla: una
 * respuesta que llega tarde y escribe en un formulario que ya no es el suyo. Se tapó sitio por
 * sitio —el guardado, el cierre, la carga del historial— y la ronda siguiente traía el de al lado:
 * el cuarto fue el botón de crear la gestión, que se había dejado fuera «porque no escribe en
 * ningún formulario» y resulta que sí, porque al acabar es él quien lo abre.
 *
 * Dirección cerró que el flujo no cambia —la promesa se anota al cerrar la gestión, en la misma
 * pantalla, y al crear la gestión el formulario se sigue abriendo solo— y que **la garantía vive en
 * un solo sitio por el que pasan todos los caminos**, no repartida por cada botón. Esto es ese
 * sitio.
 *
 *   const formulario = formularioEnUso({
 *     abierto: function () { return self.promesa.abierto; },   // el estado lo sigue teniendo la
 *     cerrar:  function () { self.promesa.abierto = false; },  // pantalla: aquí no se copia
 *     carriles: [self.carrilPromesa, self.carrilPromesaGuardado]
 *   });
 *
 *   formulario.abrir({ porLaPersona: true }, function () { ...volcar los campos... });
 *   formulario.abrir({}, function () { ...lo mismo... });   // una respuesta que no sale de ninguno
 *   formulario.cerrar({ turno: turnoAlGuardar });           // la respuesta de SU formulario
 *   formulario.cerrar({ siEsDe: function () { ... } });     // el de ESTA operación, si es el de delante
 *
 * ## Quién pide: cuatro maneras de decirlo, y ninguna por omisión
 *
 * - **`{ porLaPersona: true }`** · la dueña. Lo que pide, se hace.
 * - **`{ turno: N }`** · una petición que salió **de dentro** del formulario de la sesión `N` —el
 *   guardado de la promesa, la carga de su desplegable—. Manda sobre ése y sobre ninguno más.
 * - **`{ siEsDe: fn }`** · una petición que no salió de ningún formulario pero que habla de **una
 *   operación concreta**, y solo manda si el que hay delante es el de esa operación. Lo decide la
 *   pantalla, que es la que sabe de qué es cada formulario, y lo pregunta esta utilidad, que es
 *   donde vive la decisión. Existe por la reconciliación de una escritura en duda: cuando el
 *   servidor confirma que la promesa **sí quedó guardada**, su formulario tiene que irse de delante
 *   —si no, la persona sigue viendo esos datos, pulsa otra vez y, con la duda ya resuelta, esa
 *   pulsación sale con una referencia nueva y guarda una segunda promesa (revisión CODEX, ronda 11)—
 *   y esa respuesta no tiene turno, porque no salió del formulario. Lo que **no** puede es cerrar el
 *   borrador de otra factura que alguien haya abierto entretanto, y eso es lo que la condición dice.
 * - **`{}`** · una petición que **no salió de ningún formulario**, como la creación de la gestión,
 *   que sale de la lista. No es dueña de nada: solo puede abrir si no hay ninguno delante.
 *
 * Lo que se omite cae en la opción más restrictiva, que es justo al revés de lo que pasaba antes:
 * el camino que faltaba se había dejado fuera por descuido y eso le daba barra libre.
 *
 * ## Las dos puertas, y por qué son dos y no siete
 *
 * 1. **`abrir` y `cerrar`**, por donde pasa quien quiere poner o quitar el formulario de delante.
 *    Las dos preguntan lo mismo (`manda`) y es la única decisión que hay aquí.
 * 2. **El turno, que viaja a los carriles** (`carril.sesion(turno)`). Todo lo que una petición
 *    escriba *dentro* del formulario —lo que carga el desplegable, el error de su guardado— va por
 *    un carril, y abrir o cerrar el formulario sube el turno: lo que quedara en vuelo de la sesión
 *    anterior del formulario deja de poder escribir en la de ahora y su respuesta sale por
 *    `descartada`.
 *
 * Esa segunda puerta es la misma decisión de diseño que el sello de sesión de `carrilPeticiones`
 * (#262, dirección 06/10/2026): la vigencia vive **dentro** del carril en vez de en una guarda del
 * componente al lado, porque dos vigilantes que no se hablan dejan una juntura, y esa juntura dio
 * cuatro rondas de revisión. Aquí el turno lo pone esta utilidad y lo aplica el carril: una sola
 * verdad sobre «de qué sesión del formulario es esta respuesta».
 *
 * ## El estado NO se copia
 *
 * Si el formulario está abierto lo dice la pantalla (`acciones.abierto()`), no una copia de aquí.
 * Un booleano repetido en dos sitios es otra juntura: basta que un camino nuevo ponga el de la
 * pantalla y se olvide del de la utilidad para que ésta empiece a proteger un formulario que ya no
 * está, o a dejar reiniciar uno que sí.
 */
export default function formularioEnUso(acciones) {
  const config = acciones || {};
  const carriles = config.carriles || [];
  // Sube en cada apertura y en cada cierre: identifica la sesión del formulario que hay delante.
  // Empieza en 0 y la primera apertura lo pone a 1, así que ninguna petición lanzada antes de que
  // hubiera formulario puede coincidir con el turno de uno.
  let turno = 0;

  function hayUnoDelante() {
    return !!config.abierto();
  }

  /**
   * LA ÚNICA DECISIÓN de esta utilidad: ¿manda quien pide sobre el formulario que hay delante?
   *
   * - **La persona manda siempre.** Es la dueña: si pulsa «Anotar promesa», quiere ese formulario.
   * - **Si no hay ninguno delante, no hay nada que proteger**, así que una respuesta puede abrirlo.
   *   Es lo que conserva el flujo que pidió dirección: crear la gestión abre el formulario solo.
   * - **Si hay uno delante, una respuesta solo manda sobre el suyo**: el de la sesión de la que
   *   salió. Sobre cualquier otro no escribe, aunque sea de la misma factura —era el agujero que
   *   quedaba— y aunque la sesión de entonces siga siendo la de ahora: quien no salió de un
   *   formulario no trae turno, y `turno` nunca es `undefined` cuando hay uno delante.
   * - **Y una respuesta que habla de una operación concreta manda si el de delante es el de esa
   *   operación** (`siEsDe`). Se pregunta después del turno a propósito: es la condición más débil
   *   de las tres, así que no puede tapar a ninguna.
   */
  function manda(quien) {
    const peticion = quien || {};
    if (peticion.porLaPersona) return true;
    if (!hayUnoDelante()) return true;
    if (peticion.turno === turno) return true;
    return !!peticion.siEsDe && !!peticion.siEsDe();
  }

  function nuevaSesion() {
    turno += 1;
    for (let i = 0; i < carriles.length; i++) carriles[i].sesion(turno);
  }

  return {
    /** El turno de ahora, que es lo que una petición tiene que guardarse al salir. */
    turno: function () {
      return turno;
    },

    /**
     * Pone el formulario delante y vuelca sus campos, si quien lo pide manda.
     *
     * `volcar` lo llama esta utilidad y **solo** ella: es lo que hace que no se pueda reiniciar el
     * formulario sin pasar por la decisión. Recibe el turno nuevo, que es el que la petición que
     * salga de ahí dentro tiene que guardarse.
     *
     * @returns {Boolean} si se ha abierto. A `false`, la pantalla tiene que decir qué ha pasado:
     *          la operación que lo pedía sí ha ocurrido, lo que no ha ocurrido es abrirlo.
     */
    abrir: function (quien, volcar) {
      if (!manda(quien)) return false;
      nuevaSesion();
      if (volcar) volcar(turno);
      return true;
    },

    /**
     * Quita el formulario de delante, si quien lo pide manda.
     *
     * La persona cierra el popup; una respuesta solo cierra el formulario que ella misma abrió —el
     * éxito de su guardado—, nunca el que la persona haya abierto después.
     */
    cerrar: function (quien) {
      if (!manda(quien)) return false;
      // El turno sube también al cerrar: lo que siga en vuelo de esta sesión —el POST que ya ha
      // salido y no se puede cancelar— deja de escribir en el formulario, y su respuesta sale por
      // `descartada` para que la pantalla cuente lo que ha pasado fuera de ninguna ficha.
      if (hayUnoDelante()) nuevaSesion();
      if (config.cerrar) config.cerrar();
      return true;
    }
  };
}
